import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { UserRole } from '@cls/shared';
import { api } from '../../lib/api.js';
import { toast } from '../../lib/toast.js';
import { SearchableSelect } from '../primitives/SearchableSelect.js';

const ROLE_OPTIONS: { value: UserRole; label: string }[] = [
  { value: UserRole.Admin, label: 'Admin — Full Access' },
  { value: UserRole.Manager, label: 'Manager — Read-only (+ WA/email)' },
  { value: UserRole.Supervisor, label: 'Supervisor — Read-only, own sites' },
  { value: UserRole.Worker, label: 'Worker — Self-service only' },
];

interface ProjectRef { id: string; code: string; name: string }
interface CustomerRef { id: string; name: string }
interface ContactRef { id: string; name: string; email: string | null }
interface WorkerRef { id: string; name: string; role: string | null }
interface AgencyRef { id: string; code: string; name: string; active: boolean }

export interface UserInitial {
  id?: string;
  email?: string;
  name?: string;
  role?: UserRole;
  active?: boolean;
  scopedCustomerIds?: string[];
  scopedProjectIds?: string[];
  scopedAgencyCode?: string | null;
  workerId?: string | null;
  contactId?: string | null;
}

interface Props {
  onClose: () => void;
  initial?: UserInitial;
  /** When true, role + active fields are disabled (used when editing the current user). */
  selfEdit?: boolean;
}

function CheckboxList<T extends { id: string }>({
  items,
  labelOf,
  selected,
  onChange,
}: {
  items: T[];
  labelOf: (item: T) => string;
  selected: string[];
  onChange: (next: string[]) => void;
}) {
  const set = new Set(selected);
  const toggle = (id: string) => {
    const next = new Set(set);
    if (next.has(id)) next.delete(id); else next.add(id);
    onChange([...next]);
  };
  return (
    <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: 8, maxHeight: 200, overflowY: 'auto' }}>
      {items.length === 0 ? (
        <div style={{ fontSize: 11, color: 'var(--muted)', padding: 4 }}>None available.</div>
      ) : items.map((item) => (
        <label
          key={item.id}
          style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', fontSize: 11, cursor: 'pointer', borderRadius: 5 }}
          onMouseEnter={(e) => (e.currentTarget.style.background = 'rgba(232,164,39,.06)')}
          onMouseLeave={(e) => (e.currentTarget.style.background = '')}
        >
          <input
            type="checkbox"
            checked={set.has(item.id)}
            onChange={() => toggle(item.id)}
            style={{ accentColor: 'var(--accent)' }}
          />
          <span>{labelOf(item)}</span>
        </label>
      ))}
    </div>
  );
}

export function AddUserModal({ onClose, initial, selfEdit }: Props) {
  const qc = useQueryClient();
  const editing = !!initial?.id;

  const [email, setEmail] = useState(initial?.email ?? '');
  const [name, setName] = useState(initial?.name ?? '');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>(initial?.role ?? UserRole.Manager);
  const [active, setActive] = useState(initial?.active ?? true);
  const [scopedCustomerIds, setScopedCustomerIds] = useState<string[]>(initial?.scopedCustomerIds ?? []);
  const [scopedProjectIds, setScopedProjectIds] = useState<string[]>(initial?.scopedProjectIds ?? []);
  const [scopedAgencyCode, setScopedAgencyCode] = useState<string>(initial?.scopedAgencyCode ?? '');
  const [workerId, setWorkerId] = useState<string>(initial?.workerId ?? '');
  const [contactId, setContactId] = useState<string>(initial?.contactId ?? '');

  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') onClose(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const { data: workers = [] } = useQuery({
    queryKey: ['workers'],
    queryFn: () => api.get<WorkerRef[]>('/workers').then((r) => r.data),
    enabled: role === UserRole.Worker || role === UserRole.Supervisor,
  });

  const sortedWorkers = useMemo(() => [...workers].sort((a, b) => a.name.localeCompare(b.name)), [workers]);
  const { data: projects = [] } = useQuery({
    queryKey: ['projects'],
    queryFn: () => api.get<{ id: string; code: string; name: string }[]>('/projects').then((r) => r.data),
    enabled: role === UserRole.Supervisor,
  });

  const emailValid = /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
  const passValid = editing || password.length >= 8;
  const nameValid = name.trim().length > 0;
  const roleConditionsMet =
    (role !== UserRole.Worker || workerId.trim().length > 0) && (role !== UserRole.Supervisor || scopedProjectIds.length > 0);

  const save = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {
        email: email.trim().toLowerCase(),
        name: name.trim(),
        role,
        active,
        scopedCustomerIds: [],
        scopedProjectIds: role === UserRole.Supervisor ? scopedProjectIds : [],
        scopedAgencyCode: null,
        workerId: role === UserRole.Worker || role === UserRole.Supervisor ? (workerId || null) : null,
        contactId: null,
      };
      if (!editing) body.password = password;
      if (editing && initial?.id) return api.patch(`/users/${initial.id}`, body);
      return api.post('/users', body);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['users'] });
      toast.success(editing ? 'User updated' : 'User created', name);
      onClose();
    },
    onError: (e: { response?: { data?: { message?: string } } }) =>
      toast.error('Save failed', e?.response?.data?.message ?? 'Check fields and try again'),
  });

  const canSave = emailValid && nameValid && passValid && roleConditionsMet && !save.isPending;

  return createPortal(
    <div className="mo open" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-title">{editing ? '✏ Edit User' : '+ New User'}</div>
        {selfEdit ? (
          <div className="modal-sub">You're editing your own account — role and active status are locked.</div>
        ) : null}

        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Email</label>
            <input
              type="email"
              className={`form-input${email && !emailValid ? ' form-input-err' : ''}`}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="off"
            />
            {editing ? (
              <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 3 }}>
                Changing the email updates the login identity — no welcome mail is re-sent.
              </div>
            ) : null}
          </div>
          <div className="form-row">
            <label className="form-label">Full Name</label>
            <input type="text" className="form-input" value={name} onChange={(e) => setName(e.target.value)} />
          </div>
        </div>

        {!editing ? (
          <div className="form-row">
            <label className="form-label">Password (min 8 chars)</label>
            <input
              type="password"
              className={`form-input${password && !passValid ? ' form-input-err' : ''}`}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              autoComplete="new-password"
            />
            <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 3 }}>
              The user can change it after their first login.
            </div>
          </div>
        ) : null}

        <div className="fg2">
          <div className="form-row">
            <label className="form-label">Role</label>
            <SearchableSelect
              value={role}
              onChange={(v) => setRole(v as UserRole)}
              options={ROLE_OPTIONS}
              disabled={selfEdit}
            />
          </div>
          <div className="form-row">
            <label className="form-label">Account</label>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                background: 'var(--card)',
                border: '1px solid var(--border)',
                borderRadius: 8,
                padding: '8px 10px',
                cursor: selfEdit ? 'not-allowed' : 'pointer',
                opacity: selfEdit ? 0.6 : 1,
                fontSize: 12,
              }}
            >
              <input
                type="checkbox"
                checked={active}
                disabled={selfEdit}
                onChange={(e) => setActive(e.target.checked)}
                style={{ accentColor: 'var(--accent)' }}
              />
              <span>{active ? 'Active' : 'Inactive'}</span>
            </label>
          </div>
        </div>

        {role === UserRole.Worker || role === UserRole.Supervisor ? (
          <div className="form-row">
            <label className="form-label">Linked Worker{role === UserRole.Supervisor ? ' (optional)' : ''}</label>
            <SearchableSelect
              value={workerId}
              onChange={setWorkerId}
              placeholder="— Select worker —"
              options={[...(role === UserRole.Supervisor ? [{ value: '', label: '— No worker record —' }] : []), ...sortedWorkers.map((w) => ({ value: w.id, label: w.role ? `${w.name} — ${w.role}` : w.name }))]}
              invalid={role === UserRole.Worker && !workerId}
            />
            <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 3 }}>
              {role === UserRole.Worker ? 'Worker users can only sign in for the worker record they are linked to.' : 'Link the supervisor\'s own worker record so they can sign in and mark availability.'}
            </div>
          </div>
        ) : null}

        {role === UserRole.Supervisor ? (
          <div className="form-row">
            <label className="form-label">Sites this supervisor runs</label>
            <div id="user-sites" style={{ background: 'var(--card)', border: `1px solid ${scopedProjectIds.length ? 'var(--border)' : 'var(--danger)'}`, borderRadius: 8, padding: 10, maxHeight: 180, overflowY: 'auto', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 4 }}>
              {projects.map((p) => (
                <label key={p.id} style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 11, cursor: 'pointer', padding: 4, borderRadius: 4, background: scopedProjectIds.includes(p.id) ? 'rgba(232,164,39,.15)' : 'var(--surface)' }}>
                  <input type="checkbox" checked={scopedProjectIds.includes(p.id)} onChange={() => setScopedProjectIds((cur) => cur.includes(p.id) ? cur.filter((x) => x !== p.id) : [...cur, p.id])} style={{ accentColor: 'var(--accent)' }} />
                  <span style={{ flex: 1 }}>{p.code} — {p.name}</span>
                </label>
              ))}
            </div>
            <div style={{ fontSize: 10, color: 'var(--faint)', marginTop: 3 }}>
              They see the workers deployed on these sites, with notes and certificates but no ratings.
            </div>
          </div>
        ) : null}

        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => save.mutate()} disabled={!canSave}>
            {save.isPending ? 'Saving…' : (editing ? 'Save Changes' : 'Create User')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
